import React, { useCallback, useState } from 'react';

import classnames from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { emptyObject } from 'ui-components/default-props/empty';
import { ModalFrameworkMethodsPropType } from 'ui-components/prop-types/modal-framework-methods';

import messages from './messages';
import { OrderSummaryInformation } from './order-summary-information';
import { RecipientInformation } from './recipient-information';
import EditableOrderItemsContainer from '../../../components/order-items-list/editable-order-items/component-state-manager';
import Global from '../../../store/global-selectors';
import { newOrderModalHasMadeChanges } from '../../../store/orders/edit-order/edit.order.actions';
import * as OrderActions from '../../../store/orders/orders.actions';
import { orderPropertyToValidatorMap } from '../../../store/orders/orders.helpers';
import { DEMO_STORE } from '../../../demos/demo-orders';

import style from './new-order-modal.module.less';

const objectHasErrors = (errorsObj) => Object.keys(errorsObj).some((key) => errorsObj[key] === true);

const NewOrderModal = ({ workflowGuid }) => {
  const dispatch = useDispatch();

  const hasMadeChanges = useSelector(Global.selectEditOrdergetEditOrderHasMadeChanges);
  const loading = useSelector(Global.selectEditOrdergetEditOrderLoading);

  const stores = useSelector(Global.selectSellerStores).filter((store) => store.isActive && store.marketplaceId === 0);
  const reduxOrder = useSelector(Global.selectEditOrdergetEditOrderOrder) || emptyObject;
  if (reduxOrder && reduxOrder.store && !reduxOrder.store.storeGuid && stores.length) {
    const nonDemoStores = stores.filter((store) => store.name !== DEMO_STORE);
    [reduxOrder.store] = nonDemoStores;
  }

  const selectedCustomersIds = useSelector(Global.selectSelectedCustomersIds);
  const selectedCustomers = useSelector((state) =>
    Global.selectEntitiesCustomersByIds(state, {
      customerIds: selectedCustomersIds,
    }),
  );
  const selectedCustomersCount = useSelector(Global.selectSelectedCustomersCount);
  const selectedCustomer = selectedCustomersCount < 1 ? emptyObject : selectedCustomers[0];

  const storeGuid = reduxOrder.store && reduxOrder.store.storeGuid ? reduxOrder.store.storeGuid : '';
  const shipToAddress = {
    ...reduxOrder.shipToAddress,
    ...selectedCustomer.address,
    email: selectedCustomer.email || '',
  };
  const selectedStore = stores.find((store) => store.storeGuid === storeGuid);

  const [order, setOrder] = useState({
    ...reduxOrder,
    shipToAddress,
    storeGuid,
  });
  const [autoGenerateOrderNumber, setAutoGenerateOrderNumber] = useState(selectedStore ? selectedStore.willAutoGenOrderNumber : true);
  const [fieldsWithError, setFieldsWithError] = useState(emptyObject);
  const [orderItems, setOrderItems] = useState([]);
  const [itemsHaveValidationError, setItemsHaveValidationError] = useState(false);

  const validateOrder = useCallback(
    (handler) => {
      const newOrder = {
        ...order,
        soldTo: {
          ...order.soldTo,
          name: order.shipToAddress.name,
          phone: order.shipToAddress.phone,
          email: order.shipToAddress.email,
        },
        store: {
          storeGuid: order.storeGuid,
        },
      };
      let newFieldsWithOrder = { ...fieldsWithError };

      Object.keys(orderPropertyToValidatorMap).forEach((fieldName) => {
        const errorsObj = orderPropertyToValidatorMap[fieldName](newOrder);
        newFieldsWithOrder = { ...newFieldsWithOrder, ...errorsObj };
      });
      if (autoGenerateOrderNumber) {
        newFieldsWithOrder.orderNumber = false;
      }
      const formHasErrors = objectHasErrors(newFieldsWithOrder);

      if (formHasErrors || itemsHaveValidationError) {
        setItemsHaveValidationError(true);
        setFieldsWithError(newFieldsWithOrder);
        return;
      }
      const newOrderWithItems = {
        ...newOrder,
        items: orderItems,
      };

      dispatch(OrderActions.createOrder(newOrderWithItems, handler, workflowGuid));
    },
    [order, fieldsWithError, autoGenerateOrderNumber, dispatch, workflowGuid, itemsHaveValidationError, orderItems],
  );

  const updateOrder = useCallback(
    (newOrder) => {
      setOrder(newOrder);
      if (!hasMadeChanges) {
        dispatch(newOrderModalHasMadeChanges());
      }
    },
    [dispatch, hasMadeChanges],
  );

  return (
    <Modal className={style['new-manual-order-modal']}>
      <ModalHeader className={style['new-order-modal-header']}>
        <FormattedMessage {...messages.title} />
      </ModalHeader>
      <ModalBody className={style['modal-body']}>
        {loading && <LoadingIndicator className={style['loading-indicator']} />}
        {!loading && (
          <div className={style['order-content']}>
            <div className={style['recipient-and-order-summary-container']}>
              <RecipientInformation
                fieldsWithError={fieldsWithError}
                order={order}
                setFieldsWithError={setFieldsWithError}
                setOrder={updateOrder}
              />
              <OrderSummaryInformation
                autoGenerateOrderNumber={autoGenerateOrderNumber}
                fieldsWithError={fieldsWithError}
                order={order}
                setAutoGenerateOrderNumber={setAutoGenerateOrderNumber}
                setOrder={updateOrder}
              />
            </div>
            <div className={style['order-items-container']}>
              <div className={classnames(style['section-title'], style['order-items-title'])}>
                <FormattedMessage {...messages.orderItemsTitle} />
              </div>
              <EditableOrderItemsContainer
                onChange={setOrderItems}
                onValidationChange={setItemsHaveValidationError}
                order={order}
                shouldShowValidationErrors={itemsHaveValidationError}
              />
            </div>
          </div>
        )}
      </ModalBody>
      {!loading && (
        <ModalFooter className={style['new-manual-order-modal-footer']}>
          <ModalFooterCancelButton />
          <div className={classnames('button-group', style['right-buttons'])}>
            <div className={classnames('button-group', style['button-container'])}>
              <Button className={style['button-styles']} onClick={() => validateOrder('save')} theme='primary'>
                <FormattedMessage {...messages.save} />
              </Button>
              <DropdownMenu dropdownMenuClassName={style['dropdown-menu']} menuPosition='bottom-right'>
                <DropdownMenuToggler className={style['drop-down-menu-button']} theme='primary' />
                <DropdownMenuItem onClick={() => validateOrder('createAnother')}>
                  <FormattedMessage {...messages.saveAndCreateAnother} className={style['dropdown-item']} />
                </DropdownMenuItem>
              </DropdownMenu>
            </div>
          </div>
        </ModalFooter>
      )}
    </Modal>
  );
};

NewOrderModal.propTypes = {
  modalFrameworkMethods: ModalFrameworkMethodsPropType.isRequired,
  workflowGuid: PropTypes.string.isRequired,
};

export default NewOrderModal;
